import { useTheme } from '../../hooks/useTheme';
import { Tile, Button, Table, Row, Cell, Dropdown, MenuItem, Dialog, Input } from '@dsi/react-eds';
import ReactEDSWrapper from '../../utils/react-eds-wrapper';
import { useState, useEffect } from 'react';
import { useSwobNotifications } from '../../hooks/NotificationsProvider';
import { AxiosError } from 'axios';
import { eppEnmService, EppEnm } from '../../services/eppEnmService';
import { eppConfig } from '../../config/eppConfig';

/**
 * EPP-sourced ENM endpoint management (RLCS-2409).
 *
 * Rendered only when EPP is enabled for the environment (eppConfig). An EPP ENM
 * endpoint is just a name (+ optional description) — no url/credentials/cluster —
 * because the customer/tech are environment constants supplied at report time.
 * Kept separate from the live-ENM page so the complex live-ENM flow is untouched.
 */
const EppEnms: React.FC = () => {
  const { theme } = useTheme();
  const { addNotifications } = useSwobNotifications();

  const [items, setItems] = useState<EppEnm[]>([]);
  const [loading, setLoading] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState<EppEnm | null>(null);
  const [formName, setFormName] = useState('');
  const [formDescription, setFormDescription] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (eppConfig.enabled) fetchItems();
  }, []);

  const notifyError = (fallback: string, error: unknown) =>
    addNotifications([
      {
        title: 'Error',
        icon: 'icon-triangle-warning',
        description: error instanceof AxiosError ? error.response?.data?.message || error.message : fallback,
        timestamp: new Date(),
        color: 'red'
      }
    ]);

  const fetchItems = async () => {
    setLoading(true);
    try {
      const response = await eppEnmService.getAll();
      setItems(response.data || []);
    } catch (error) {
      notifyError('Failed to load EPP ENM endpoints.', error);
    } finally {
      setLoading(false);
    }
  };

  const openCreate = () => {
    setEditing(null);
    setFormName('');
    setFormDescription('');
    setDialogOpen(true);
  };

  const openEdit = (item: EppEnm) => {
    setEditing(item);
    setFormName(item.name);
    setFormDescription(item.description ?? '');
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!formName.trim()) return;
    setSaving(true);
    try {
      const payload = { name: formName.trim(), description: formDescription.trim() || undefined };
      if (editing) {
        await eppEnmService.update(editing.id, payload);
      } else {
        await eppEnmService.create(payload);
      }
      setDialogOpen(false);
      await fetchItems();
      addNotifications([
        {
          title: 'Saved',
          icon: 'icon-check',
          description: `EPP ENM endpoint ${editing ? 'updated' : 'created'}.`,
          timestamp: new Date(),
          color: 'green'
        }
      ]);
    } catch (error) {
      notifyError('Failed to save EPP ENM endpoint.', error);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (item: EppEnm) => {
    try {
      await eppEnmService.remove(item.id);
      await fetchItems();
    } catch (error) {
      notifyError('Failed to delete EPP ENM endpoint.', error);
    }
  };

  // Defensive: never render if EPP disabled (page-level gating also applies).
  if (!eppConfig.enabled) return null;

  const columns = [
    { title: 'Name', attribute: 'name', width: '35%' },
    { title: 'Description', attribute: 'description', width: '55%' }
  ];

  return (
    <div className={`${theme ? 'light-theme' : 'dark-theme'}`}>
      <div className="row">
        <div className="column sm-12">
          <ReactEDSWrapper component={Tile} title="EPP ENM Endpoints" id="epp-enms-tile">
            <div style={{ marginBottom: '16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <ReactEDSWrapper component={Button} primary onClick={openCreate}>
                <i className="icon icon-plus" /> Create EPP ENM
              </ReactEDSWrapper>
              <div style={{ color: '#6a6a6a' }}>
                {items.length} endpoint{items.length !== 1 ? 's' : ''}
              </div>
            </div>

            {loading && (
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <div className="loader small" />
                <p>Loading...</p>
              </div>
            )}

            {!loading && items.length === 0 && (
              <p style={{ color: '#6a6a6a' }}>No EPP ENM endpoints yet. Create one to use it in a report.</p>
            )}

            {!loading && items.length > 0 && (
              <ReactEDSWrapper component={Table} columns={columns} rowActions>
                {items.map((item) => (
                  <ReactEDSWrapper
                    key={item.id}
                    component={Row}
                    id={item.id}
                    actions={[
                      <ReactEDSWrapper component={Dropdown} more>
                        <ReactEDSWrapper component={MenuItem} onClick={() => openEdit(item)}>
                          <i className="icon icon-edit" style={{ marginRight: '8px' }} />
                          Edit
                        </ReactEDSWrapper>
                        <ReactEDSWrapper component={MenuItem} onClick={() => handleDelete(item)}>
                          <i className="icon icon-trashcan" style={{ marginRight: '8px' }} />
                          Delete
                        </ReactEDSWrapper>
                      </ReactEDSWrapper>
                    ]}
                  >
                    <ReactEDSWrapper component={Cell}>
                      <div style={{ fontWeight: '500' }}>{item.name}</div>
                    </ReactEDSWrapper>
                    <ReactEDSWrapper component={Cell}>
                      <div style={{ color: '#6a6a6a' }}>{item.description || '—'}</div>
                    </ReactEDSWrapper>
                  </ReactEDSWrapper>
                ))}
              </ReactEDSWrapper>
            )}
          </ReactEDSWrapper>
        </div>
      </div>

      {dialogOpen && (
        <ReactEDSWrapper
          component={Dialog}
          show={dialogOpen}
          onClose={() => setDialogOpen(false)}
          title={editing ? 'Edit EPP ENM Endpoint' : 'Create EPP ENM Endpoint'}
          bottom={
            <ReactEDSWrapper component={Button} primary onClick={handleSave} disabled={!formName.trim() || saving}>
              {saving ? 'Saving…' : 'Save'}
            </ReactEDSWrapper>
          }
        >
          <div style={{ display: 'flex', flexDirection: 'column', gap: '16px', minWidth: '360px' }}>
            <label style={{ display: 'block' }}>
              <span style={{ display: 'block', marginBottom: '4px', fontSize: '14px' }}>ENM name</span>
              <ReactEDSWrapper
                component={Input}
                id="epp-enm-name"
                type="text"
                placeholder="e.g., ENM name"
                value={formName}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setFormName(e.target.value)}
              />
            </label>
            <label style={{ display: 'block' }}>
              <span style={{ display: 'block', marginBottom: '4px', fontSize: '14px' }}>Description (optional)</span>
              <ReactEDSWrapper
                component={Input}
                id="epp-enm-description"
                type="text"
                placeholder="Optional description"
                value={formDescription}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setFormDescription(e.target.value)}
              />
            </label>
          </div>
        </ReactEDSWrapper>
      )}
    </div>
  );
};

export default EppEnms;
